import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const PathController = ({
  data,
  setDataForTable,
  setFilteredData,
  openStates,
  setOpenStates,
}) => {
  // State for search inputs
  const [searchValues, setSearchValues] = useState({
    PT_DATE: "",
    PT_FROM: "",
    PT_TO: "",
    PT_TYPE: "",
    PT_REASON: "",
  });

  // State for selected filters
  const [selectedDate, setSelectedDate] = useState(new Set());
  const [selectedFrom, setSelectedFrom] = useState(new Set());
  const [selectedTo, setSelectedTo] = useState(new Set());
  const [selectedType, setSelectedType] = useState(new Set());
  const [selectedReason, setSelectedReason] = useState(new Set());

  // Update table data based on selected filters
  useEffect(() => {
    setDataForTable({
      date: Array.from(selectedDate),
      from: Array.from(selectedFrom),
      to: Array.from(selectedTo),
      type: Array.from(selectedType),
      reason: Array.from(selectedReason),
    });
  }, [
    selectedDate,
    selectedFrom,
    selectedTo,
    selectedType,
    selectedReason,
    setDataForTable,
  ]);

  // Toggle selection for checkboxes
  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  // Toggle dropdown visibility
  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Handle search input changes
  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Initialize filter options
  const defaultSetup = () => {
    if (!data?.length) return;
    const allDates = [
      ...new Set(
        data.map((d) => d.PT_DATE?.slice(0, 10)).filter((v) => v != null)
      ),
    ];
    const allFrom = [
      ...new Set(data.map((d) => d.PT_FROM).filter((v) => v != null)),
    ];
    const allTo = [
      ...new Set(data.map((d) => d.PT_TO).filter((v) => v != null)),
    ];
    const allTypes = [
      ...new Set(data.map((d) => d.PT_TYPE).filter((v) => v != null)),
    ];
    const allReasons = [
      ...new Set(data.map((d) => d.PT_REASON).filter((v) => v != null)),
    ];

    setSelectedDate(new Set(allDates));
    setSelectedFrom(new Set(allFrom));
    setSelectedTo(new Set(allTo));
    setSelectedType(new Set(allTypes));
    setSelectedReason(new Set(allReasons));
    setSearchValues({
      PT_DATE: "",
      PT_FROM: "",
      PT_TO: "",
      PT_TYPE: "",
      PT_REASON: "",
    });
    setOpenStates({
      PT_DATE: false,
      PT_FROM: false,
      PT_TO: false,
      PT_TYPE: false,
      PT_REASON: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Available options for each filter
  const availableDates = useMemo(() => {
    return [
      ...new Set(
        data.map((d) => d.PT_DATE?.slice(0, 10)).filter((v) => v != null)
      ),
    ];
  }, [data]);

  const availableFrom = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter((entry) => selectedDate.has(entry.PT_DATE?.slice(0, 10)))
          .map((d) => d.PT_FROM)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedDate]);

  const availableTo = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedDate.has(entry.PT_DATE?.slice(0, 10)) &&
              selectedFrom.has(entry.PT_FROM)
          )
          .map((d) => d.PT_TO)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedDate, selectedFrom]);

  const availableTypes = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedDate.has(entry.PT_DATE?.slice(0, 10)) &&
              selectedFrom.has(entry.PT_FROM) &&
              selectedTo.has(entry.PT_TO)
          )
          .map((d) => d.PT_TYPE)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedDate, selectedFrom, selectedTo]);

  const availableReasons = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedDate.has(entry.PT_DATE?.slice(0, 10)) &&
              selectedFrom.has(entry.PT_FROM) &&
              selectedTo.has(entry.PT_TO) &&
              selectedType.has(entry.PT_TYPE)
          )
          .map((d) => d.PT_REASON)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedDate, selectedFrom, selectedTo, selectedType]);

  // Filtered data based on selections
  const filteredData = useMemo(() => {
    if (
      !data ||
      !Array.isArray(data) ||
      selectedDate.size === 0 ||
      selectedFrom.size === 0 ||
      selectedTo.size === 0 ||
      selectedType.size === 0 ||
      selectedReason.size === 0
    ) {
      return [];
    }

    return data.filter((entry) => {
      return (
        selectedDate.has(entry.PT_DATE?.slice(0, 10)) &&
        selectedFrom.has(entry.PT_FROM) &&
        selectedTo.has(entry.PT_TO) &&
        selectedType.has(entry.PT_TYPE) &&
        selectedReason.has(entry.PT_REASON)
      );
    });
  }, [
    data,
    selectedDate,
    selectedFrom,
    selectedTo,
    selectedType,
    selectedReason,
  ]);

  // Propagate filteredData to parent component
  useEffect(() => {
    setFilteredData(filteredData);
  }, [filteredData, setFilteredData]);

  // Render checkbox list for each filter
  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        ?.toLowerCase()
        ?.includes(searchValues[title]?.toLowerCase() || "")
    );

    return (
      <div className="col-md mt-2 p-1">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="papaDiv"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title} {"  "}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="papaDiv"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title] || ""}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sx fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(setFunc, selectedSet, item)
                          }
                        />{" "}
                        {item || "N/A"}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(filteredItems))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="papaDiv">
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "PT_DATE",
          availableDates,
          selectedDate,
          setSelectedDate
        )}
        {renderCheckboxList(
          "PT_FROM",
          availableFrom,
          selectedFrom,
          setSelectedFrom
        )}
        {renderCheckboxList("PT_TO", availableTo, selectedTo, setSelectedTo)}
        {renderCheckboxList(
          "PT_TYPE",
          availableTypes,
          selectedType,
          setSelectedType
        )}
        {renderCheckboxList(
          "PT_REASON",
          availableReasons,
          selectedReason,
          setSelectedReason
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-reset">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 3px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default PathController;
